/**
 * Interim messages - the engine's short conversational lines during a run.
 *
 * "Got it - drafting the settlement agreement now." … "Writing it up now."
 * They arrive as ordered events while the pipeline runs AND are persisted in
 * the result blob, so a reloaded turn reads the same as it did live.
 *
 * The engine has not frozen the event name yet, so the mapping below is
 * deliberately tolerant: any `research.*` event carrying an interim-shaped
 * payload is accepted, and every spelling of the ordering key is honoured.
 * Ordering is explicit where the engine supplies it and arrival order
 * otherwise - never assumed from the text.
 */

export interface InterimMessage {
  text: string;
  /**
   * The engine's 1-based position. RENDER ORDER IS THIS, ALWAYS.
   * Pubsub delivery order is not guaranteed, so arrival order is never used
   * as the order - only as a last-resort key for a line that somehow has no
   * sequence at all.
   */
  sequence: number;
  /** `acknowledgement` | `retrieval` | `synthesis` - styling only, never order. */
  stage?: string;
}

/** Event names that carry an interim line. Any of these, or none. */
const INTERIM_EVENTS = new Set(['research.interim']);

export function isInterimEvent(eventName: string): boolean {
  return INTERIM_EVENTS.has(eventName);
}

/** The line carried by an interim event, or '' when there is none. */
export function interimText(payload: Record<string, any>, fallback = ''): string {
  const raw =
    payload?.interim_message ??
    payload?.message ??
    payload?.text ??
    payload?.content ??
    fallback;
  return String(raw ?? '').trim();
}

/** The engine's ordering key, when it sends one. */
export function interimSequence(payload: Record<string, any>, arrival: number): number {
  const raw = payload?.sequence ?? payload?.index ?? payload?.order ?? payload?.step;
  const value = Number(raw);
  return Number.isFinite(value) ? value : arrival;
}

/**
 * Append a line, in order, without duplicates.
 *
 * A re-delivered event (the pubsub layer can redeliver) must not print the
 * same sentence twice, so an identical text at the same sequence is dropped.
 */
export function addInterim(
  messages: InterimMessage[],
  text: string,
  sequence: number,
  stage?: string
): InterimMessage[] {
  const clean = text.trim();
  if (!clean) return messages;
  // A redelivered event must not print the same sentence twice. Sequence is
  // the identity: the engine numbers each line once.
  if (messages.some((m) => m.sequence === sequence)) return messages;
  return [...messages, { text: clean, sequence, stage }]
    .sort((a, b) => a.sequence - b.sequence);
}

/**
 * Merge the authoritative set from `research.completed` over what arrived
 * live.
 *
 * A client that connected late has missed some events; `completed` carries
 * every line actually emitted, so it REPLACES the live list rather than
 * being appended to it - which also means a duplicate can never survive.
 */
export function reconcileInterim(
  live: InterimMessage[],
  authoritative: InterimMessage[]
): InterimMessage[] {
  return authoritative.length > 0 ? authoritative : live;
}

/**
 * Interim lines persisted on a result blob.
 *
 * Accepts a list of strings or of objects, so whichever shape the engine
 * settles on reads back in the order it was written.
 */
export function interimFromBlob(source: any): InterimMessage[] {
  // Both spellings, as with leadIn/closing.
  const raw =
    source?.interim_messages ??
    source?.interimMessages ??
    source?.interim ??
    [];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry: any, i: number) => {
      if (typeof entry === 'string') return { text: entry.trim(), sequence: i };
      return {
        text: interimText(entry || {}),
        sequence: interimSequence(entry || {}, i),
        stage: entry?.stage,
      };
    })
    .filter((m: InterimMessage) => m.text !== '')
    .sort((a: InterimMessage, b: InterimMessage) => a.sequence - b.sequence);
}

/** Just the text, in order - what the transcript renders. */
export function interimTexts(messages: InterimMessage[]): string[] {
  return messages.map((m) => m.text);
}

/* ------------------------------------------------------------------ */
/*  Progress cues                                                      */
/* ------------------------------------------------------------------ */

/**
 * The engine's tailored progress phrases for THIS question.
 *
 * They ride the same interim channel as the narration, marked
 * `stage: "progress_cues"` and carrying a `cues: string[]` instead of a
 * sentence - so they are not a line to print in the transcript, they are the
 * vocabulary the working indicator cycles while the run is in flight
 * ("Checking the 2019 amendments", "Comparing the two clauses"). They are
 * emitted early, once, and persisted on the result blob so a reload of a
 * still-running turn gets them back.
 */
export const PROGRESS_CUE_STAGE = 'progress_cues';

/** Is this interim event the cue list rather than a narration line? */
export function isProgressCueEvent(payload: Record<string, any>): boolean {
  return (
    String(payload?.stage || '').trim().toLowerCase() === PROGRESS_CUE_STAGE ||
    Array.isArray(payload?.cues) ||
    Array.isArray(payload?.progress_cues)
  );
}

/** Clean, de-duplicated, order-preserving. Anything unusable is dropped. */
function toCueList(raw: any): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw
    .map((entry) => String(entry?.text ?? entry ?? '').trim())
    .filter((text) => {
      if (!text || seen.has(text)) return false;
      seen.add(text);
      return true;
    });
}

/** The cues carried by an event payload. */
export function cuesFromPayload(payload: Record<string, any>): string[] {
  return toCueList(payload?.cues ?? payload?.progress_cues ?? payload?.progressCues);
}

/**
 * The cues persisted on a result blob.
 *
 * Same tolerance as `interimFromBlob`: whichever spelling the engine settles
 * on reads back, and a blob without them yields none rather than throwing.
 */
export function cuesFromBlob(source: any): string[] {
  return toCueList(
    source?.progress_cues ?? source?.progressCues ?? source?.cues
  );
}
